import React from "react";
import styles from "./Footer.module.css";
import { footerData } from "./data";
import Image from "next/image";
import Link from "next/link";
import router from "next/router";
import { handleLinkClick } from "../../Config/helper";


interface FooterItem {
  url: string;
  label: string;
}
interface SocialLink {
  id: number;
  link: {
    id: number;
    label: string;
    url: string;
  };
  image: {
    id: number;
    documentId: string;
    url: string;
    alternativeText: string | null;
  };
}

type FooterProps = {
  data: any;
  headerData: any;
};

const Footer: React.FC<FooterProps> = ({ data, headerData }) => {
  const { logo } = footerData;

  return (
    <footer className={styles.footer} data-section="footer">
      <div className="container">
        <div className="row">
          <div className="col-md-5 col-lg-4 pe-4">
            <div className={styles.logoSection}>
              <Link href={logo?.link || "#"} aria-label="Logo" title={logo?.alt}>
                <Image
                  src={data?.logo?.url || logo?.src}
                  alt={data?.logo?.alternativeText || logo?.alt}
                  title={data?.logo?.alternativeText || logo?.alt}
                  className={styles.logo}
                  width={204}
                  height={40}
                  loading="lazy"
                />
              </Link>
              <p className={styles.description}>
                {data?.transforming_way_text}
              </p>

              <div className={styles.socialLinks}>
                {data?.social_links.map((link: SocialLink, index: number) => (
                  <Link
                    key={index}
                    href={link?.link?.url || "#"}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={
                      link?.link?.label || "Regenesys social-media-links"
                    }
                    title={link?.link?.label || "Regenesys social-media-links"}
                  >
                    <Image
                      src={link?.image?.url}
                      alt={
                        link?.image?.alternativeText ||
                        "Regenesys social-media-links"
                      }
                      title={
                        link?.image?.alternativeText ||
                        "Regenesys social-media-links"
                      }
                      width={24}
                      height={24}
                      priority
                    />
                  </Link>
                ))}
              </div>

              <div className={styles.contact}>
                <p>
                  Contact:
                  <Link
                    title={data?.contact?.email}
                    href={`mailto:${data?.contact?.email}`}
                  >
                    {data?.contact?.email}
                  </Link>
                  <span>|</span>
                  <span>{data?.contact?.phone_no}</span>
                </p>
              </div>
            </div>
          </div>

          <div className={`col-md-7 col-lg-8 ${styles.footerHeadingText}`}>
            <div className="row">
              {data?.footer_list?.map((footerSection: any, index: number) => (
                <div className="col-md-3" key={index}>
                  <p>{footerSection?.title}</p>
                  <ul className={styles.list_links}>
                    {footerSection?.items?.map(
                      (item: FooterItem, idx: number) => (
                        <li key={idx}>
                          {item.url && (
                            <Link
                              href={item?.url || "#"}
                              aria-label={item?.label || "footer"}
                              title={item?.label}
                              onClick={(e) =>
                                handleLinkClick(e, item?.url, router)
                              }
                            >
                              {item?.label}
                            </Link>
                          )}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.secondFooter}>
          <div className="row">
            <div className="col-md-12">
              <div className={styles.footerItems}>
                {headerData?.main_nav_links?.[0]?.nested_list_links?.length >
                  0 && (
                  <div className={styles.submenuItems}>
                    <div className={styles.megamenuFlex}>
                      <div className="row">
                        {headerData?.main_nav_links?.[0]?.nested_list_links
                          ?.filter(
                            (subItem: any) =>
                              subItem?.title?.toLowerCase()?.trim() !==
                              "school of education"
                          )
                          ?.map((subItem: any) => {
                            const isPublicManagement =
                              subItem?.title?.toLowerCase()?.trim() ===
                              "school of public management";

                            return (
                              <div
                                className={`col-md-3 ${styles.footerInnerBlocks}`}
                                key={subItem?.id}
                              >
                                {/* Title */}
                                {subItem?.url ? (
                                  <Link
                                    href={subItem?.url || "#"}
                                    className={styles.sectionHeading}
                                    title={subItem?.title}
                                  >
                                    <p>{subItem?.title}</p>
                                  </Link>
                                ) : (
                                  <p className={styles.sectionHeading}>
                                    {subItem?.title}
                                  </p>
                                )}

                                {/* List Sections */}
                                {subItem?.lists?.map(
                                  (section: any, i: number) => (
                                    <div key={i} className={styles.section}>
                                      <Link
                                        href={section?.url || "#"}
                                        className={styles.sectionTitle}
                                        title={section?.title}
                                      >
                                        <p>{section?.title}</p>
                                      </Link>
                                      {section?.items?.map(
                                        (item: any, idx: number) => (
                                          <Link
                                            key={idx}
                                            href={item?.url || "#"}
                                            className={styles.submenuItem}
                                            title={item?.label}
                                          >
                                            {item?.label}
                                          </Link>
                                        )
                                      )}
                                    </div>
                                  )
                                )}

                                {/* Direct Links */}
                                {subItem?.links?.map((link: any, i: number) => (
                                  <Link
                                    key={i}
                                    href={link?.url || "#"}
                                    className={styles.submenuItem}
                                    title={link?.label}
                                  >
                                    {link?.label}
                                  </Link>
                                ))}

                                {/* School of Education inside School of Public Management */}
                                {isPublicManagement &&
                                  headerData?.main_nav_links?.[0]?.nested_list_links
                                    ?.filter(
                                      (n: any) =>
                                        n?.title?.toLowerCase()?.trim() ===
                                        "school of education"
                                    )
                                    ?.map((eduBlock: any) => (
                                      <div
                                        key={eduBlock?.id}
                                        style={{ marginTop: 16 }}
                                      >
                                        <Link
                                          href={eduBlock?.url || "#"}
                                          title={eduBlock?.title}
                                        >
                                          <div
                                            className={styles.sectionHeading}
                                          >
                                            {eduBlock?.title}
                                          </div>
                                        </Link>

                                        {eduBlock?.lists?.map(
                                          (
                                            section: any,
                                            sectionIndex: number
                                          ) => (
                                            <div
                                              key={sectionIndex}
                                              className={styles.section}
                                            >
                                              <Link
                                                href={section?.url || "#"}
                                                className={styles.sectionTitle}
                                                title={section?.title}
                                              >
                                                <p>{section?.title}</p>
                                              </Link>
                                              {section?.items?.map(
                                                (
                                                  item: any,
                                                  itemIndex: number
                                                ) => (
                                                  <Link
                                                    key={itemIndex}
                                                    href={item?.url || "#"}
                                                    className={
                                                      styles.submenuItem
                                                    }
                                                    title={item?.label}
                                                  >
                                                    {item?.label}
                                                  </Link>
                                                )
                                              )}
                                            </div>
                                          )
                                        )}

                                        {eduBlock?.links?.map(
                                          (link: any, i: number) => (
                                            <Link
                                              key={i}
                                              href={link?.url || "#"}
                                              className={styles.submenuItem}
                                              title={link?.label}
                                            >
                                              {link?.label}
                                            </Link>
                                          )
                                        )}
                                      </div>
                                    ))}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.footerBottom}>
          <div className={styles.footerBottomRight}>
            <div className={styles.policies}>
              {data?.terms_condition_links?.map(
                (item: FooterItem, index: number) => (
                  <Link
                    key={index}
                    href={item?.url || "#"}
                    className="mx-2"
                    title={item?.label}
                    style={{
                      borderRight:
                        index < data?.terms_condition_links?.length - 1
                          ? "2px solid grey"
                          : "none",
                      paddingRight: "10px",
                    }}
                    onClick={(e) => handleLinkClick(e, item?.url, router)}
                  >
                    {item?.label}
                  </Link>
                )
              )}
            </div>
          </div>
          <div className={styles.footerBottomLeft}>
            <p className={styles.copyright}>{data?.copy_right_text}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
